import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, Chip, Stack, Button, IconButton, Badge } from '@mui/material';
import { getThemeConfig } from '../../Public/themes/themeConfig';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Lottie from 'lottie-react';
import axios from 'axios';
import addToCartAnimation from '../../../lottie/Addtocart.json';

// Animation Library
import BurgerHamburgerAnimation from '../../../lottie/BurgerHamburgerFastFoodRestaurant.json';
import BurgerAnimation from '../../../lottie/Burger.json';
import OrderCompletedAnimation from '../../../lottie/Ordercompleted.json';
import SpecialOfferAnimation from '../../../lottie/specialoffer.json';
import OrderNowAnimation from '../../../lottie/Ordernow.json';
import OrderNowAnimation2 from '../../../lottie/OrderNowanimation.json';
import OrderAnimation from '../../../lottie/Order.json';
import SetMenuAnimation from '../../../lottie/setmenu.json';
import FireAnimation from '../../../lottie/Fire.json';
import BestSaleAnimation from '../../../lottie/Bestsale.json';
import LimitedOfferAnimation from '../../../lottie/limitedoffer.json';
import BigSaleAnimation from '../../../lottie/BigSale.json';
import PepperonFireAnimation from '../../../lottie/Pepperonfire.json';
import OrderNow2Animation from '../../../lottie/ordernow2.json';

const ANIMATION_MAP = {
  'BurgerHamburgerFastFoodRestaurant.json': BurgerHamburgerAnimation,
  'Burger.json': BurgerAnimation,
  'Ordercompleted.json': OrderCompletedAnimation,
  'specialoffer.json': SpecialOfferAnimation,
  'Ordernow.json': OrderNowAnimation,
  'OrderNowanimation.json': OrderNowAnimation2,
  'Order.json': OrderAnimation,
  'setmenu.json': SetMenuAnimation,
  'Fire.json': FireAnimation,
  'Bestsale.json': BestSaleAnimation,
  'limitedoffer.json': LimitedOfferAnimation,
  'BigSale.json': BigSaleAnimation,
  'Pepperonfire.json': PepperonFireAnimation,
  'ordernow2.json': OrderNow2Animation
};

const LiveStorePreview = ({ 
  branding, 
  storeName, 
  products = [], 
  allProducts = [],
  banners = [], 
  currency = { code: 'USD', symbol: '$', name: 'US Dollar' }, 
  isMobile = false,
  cart = {},
  onAddToCart,
  onIncrement,
  onDecrement,
  cartCount = 0,
  onCartClick,
  categories,
  activeCategory,
  onCategoryChange
}) => {
  const primary = branding?.primaryColor || '#FC8019';
  const accent = branding?.accentColor || '#ff5722';
  const textColor = branding?.textColor || '#111827';
  const currencySymbol = currency?.symbol || '$';
  const bgColor = branding?.backgroundColor || '#ffffff';

  // Banner carousel state
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const videoRef = useRef(null);
  const autoPlayTimerRef = useRef(null);

  // Add to cart animation state
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [animatingProduct, setAnimatingProduct] = useState(null);
  const lottieRef = useRef(null);
  
  // Product animations visibility tracking
  const [visibleAnimations, setVisibleAnimations] = useState(new Set());
  const productRefs = useRef({});

  // Helper to build full image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    
    // Get base URL and ensure no trailing slash
    let baseURL = axios.defaults.baseURL || window.location.origin;
    if (baseURL.endsWith('/')) {
      baseURL = baseURL.slice(0, -1);
    }
    
    // Ensure path has leading slash
    const path = url.startsWith('/') ? url : `/${url}`;
    
    return `${baseURL}${path}`;
  };

  // Separate banners by placement (must be before useEffect)
  // Default to 'top' placement if placement field doesn't exist (backward compatibility)
  const carouselBanners = banners.filter(b => {
    const placement = b.placement || 'top';
    const showInCarousel = b.showInCarousel !== false;
    return showInCarousel && placement === 'top';
  });
  
  const getBannersForPlacement = (placement) => banners.filter(b => {
    const bannerPlacement = b.placement || 'top';
    const showInCarousel = b.showInCarousel !== false;
    return bannerPlacement === placement && showInCarousel;
  });


  // Intersection Observer for scroll-triggered animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const productId = entry.target.getAttribute('data-product-id');
          if (productId) {
            setVisibleAnimations((prev) => {
              const newSet = new Set(prev);
              if (entry.isIntersecting) {
                newSet.add(productId);
              } else {
                newSet.delete(productId);
              }
              return newSet;
            });
          }
        });
      },
      { threshold: 0.5 }
    );

    Object.entries(productRefs.current).forEach(([productId, ref]) => {
      if (ref) {
        ref.setAttribute('data-product-id', productId);
        observer.observe(ref);
      }
    });

    return () => observer.disconnect();
  }, [products]);

  // Auto-advance banner carousel
  useEffect(() => {
    if (carouselBanners.length <= 1) return;

    const currentBanner = carouselBanners[currentBannerIndex];
    
    // Clear existing timer
    if (autoPlayTimerRef.current) {
      clearTimeout(autoPlayTimerRef.current);
    }

    // If current banner is not a video, auto-advance after 5 seconds
    if (currentBanner?.type !== 'video') {
      autoPlayTimerRef.current = setTimeout(() => {
        setCurrentBannerIndex((prev) => (prev + 1) % carouselBanners.length);
      }, 5000);
    }

    return () => {
      if (autoPlayTimerRef.current) {
        clearTimeout(autoPlayTimerRef.current);
      }
    };
  }, [currentBannerIndex, carouselBanners]);

  // Handle video end - advance to next banner
  const handleVideoEnd = () => {
    if (carouselBanners.length > 1) {
      setCurrentBannerIndex((prev) => (prev + 1) % carouselBanners.length);
    }
  };

  const nextBanner = () => {
    setCurrentBannerIndex((prev) => (prev + 1) % carouselBanners.length);
  };

  const prevBanner = () => {
    setCurrentBannerIndex((prev) => (prev - 1 + carouselBanners.length) % carouselBanners.length);
  };

  // Render banner at specific placement
  const renderPlacementBanner = (placement) => {
    const placementBanners = getBannersForPlacement(placement);
    if (placementBanners.length === 0) return null;

    return (
      <Box sx={{ mb: 3 }}>
        {placementBanners.map((banner, idx) => (
          <Box
            key={idx}
            sx={{
              height: 120,
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: '#f3f4f6',
              mb: idx < placementBanners.length - 1 ? 2 : 0
            }}
          >
            {banner.type === 'video' ? (
              <video
                src={getImageUrl(banner.url)}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                muted
                autoPlay
                loop
                onError={(e) => { 
                  console.error('Placement banner video failed:', banner.url);
                  e.target.style.display = 'none'; 
                }}
              />
            ) : (
              <img
                src={getImageUrl(banner.url)}
                alt="Banner"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => { 
                  console.error('Placement banner image failed:', banner.url);
                  e.target.style.display = 'none'; 
                }}
              />
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const themeConfig = useMemo(() => {
    return getThemeConfig(
      branding?.desktopTheme || 'swiggy',
      branding?.mobileTheme || 'swiggy',
      isMobile
    );
  }, [branding?.desktopTheme, branding?.mobileTheme, isMobile]);

  // Use real products or show placeholder
  const displayProducts = products.length > 0 ? products.slice(0, 6) : [
    { id: 'sample1', name: 'Sample Product 1', price: 45.00, category: 'Others', imageUrl: '', isVeg: false },
    { id: 'sample2', name: 'Sample Product 2', price: 35.00, category: 'Others', imageUrl: '', isVeg: true },
    { id: 'sample3', name: 'Sample Product 3', price: 25.00, category: 'Others', imageUrl: '', isVeg: true }
  ];

  // Filter special and combo items
  // For public store, don't filter by isPublished (already filtered by backend/parent)
  const specialItems = products.filter(p => p.isSpecial);
  const comboDeals = products.filter(p => p.isCombo);

  // Use categories from props, or extract from products if not provided
  const internalCategories = useMemo(() => {
    if (products.length === 0) return ['All', 'Others'];
    const cats = [...new Set(products.map(p => p.category || 'Others'))];
    return ['All', ...cats];
  }, [products]);
  
  const displayCategories = categories || internalCategories;

  // Handle add to cart with animation
  const handleAddToCartInternal = (product) => {
    const productId = product.id || product._id;
    setAnimatingProduct(productId);
    if (lottieRef.current) {
      lottieRef.current.goToAndPlay(0, true);
    }
    
    // Reset animation after it completes
    setTimeout(() => {
      setAnimatingProduct(null);
    }, 1000);

    // Call the parent's onAddToCart if provided
    if (onAddToCart) {
      onAddToCart(product);
    }
  };

  const renderProductCard = (product) => {
    const { productCard } = themeConfig;
    const isHorizontal = productCard.style === 'horizontal' || productCard.style === 'wide' || 
                         productCard.style === 'classic' || productCard.style === 'mobile-list';
    
    const isCombo = Boolean(product.isCombo);
    const isSpecial = Boolean(product.isSpecial);
    const hasProductList = Array.isArray(product.productList) && product.productList.length > 0;
    const productId = product.id || product._id;
    const isHovered = hoveredProduct === productId;
    const isAnimating = animatingProduct === productId;
    const qty = cart[productId] || 0;
    
    // Product animation settings
    const hasAnimation = product.animation?.enabled && product.animation?.file;
    const animationData = hasAnimation ? ANIMATION_MAP[product.animation.file] : null;
    const showProductAnimation = hasAnimation && (
      product.animation.trigger === 'always' ||
      (product.animation.trigger === 'hover' && isHovered) ||
      (product.animation.trigger === 'scroll' && visibleAnimations.has(productId))
    );


    return (
      <Paper
        key={productId}
        ref={(el) => { if (el) productRefs.current[productId] = el; }}
        onMouseEnter={() => setHoveredProduct(productId)}
        onMouseLeave={() => setHoveredProduct(null)}
        sx={{
          borderRadius: `${productCard.borderRadius}px`,
          boxShadow: productCard.shadow || 'none',
          border: productCard.border || 'none',
          padding: `${productCard.padding}px`,
          display: 'flex',
          flexDirection: isHorizontal ? 'row' : 'column',
          gap: isHorizontal ? 2 : 1,
          height: '100%',
          position: 'relative',
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }
        }}
      >
        {/* Product Animation Overlay */}
        {showProductAnimation && animationData && (
          <Box
            sx={{
              position: 'absolute',
              ...(product.animation.position === 'top-left' && { top: 8, left: 8 }),
              ...(product.animation.position === 'top-right' && { top: 8, right: 8 }),
              ...(product.animation.position === 'bottom-left' && { bottom: 8, left: 8 }),
              ...(product.animation.position === 'bottom-right' && { bottom: 8, right: 8 }),
              ...(product.animation.position === 'center-overlay' && { 
                top: '50%', 
                left: '50%', 
                transform: 'translate(-50%, -50%)' 
              }),
              width: product.animation.size || 80,
              height: product.animation.size || 80,
              zIndex: 5,
              pointerEvents: 'none'
            }}
          >
            <Lottie
              animationData={animationData}
              loop={true}
              autoplay={true}
              style={{ width: '100%', height: '100%' }}
            />
          </Box>
        )}
        {/* Special/Combo Badge */}
        {(isCombo || isSpecial) && (
          <Chip
            label={isCombo ? 'COMBO' : 'SPECIAL'}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: isCombo ? '#ff9800' : '#e91e63',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.65rem',
              height: 20,
              zIndex: 1
            }}
          />
        )}
        
        {/* Image */}
        <Box
          sx={{
            width: isHorizontal ? `${productCard.imageWidth || 100}px` : '100%',
            height: `${productCard.imageHeight}px`,
            borderRadius: `${productCard.borderRadius - 4}px`,
            bgcolor: '#f3f4f6',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          {product.imageUrl ? (
            <img 
              src={getImageUrl(product.imageUrl)} 
              alt={product.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { 
                console.error('Product image failed to load:', product.imageUrl);
                e.target.style.display = 'none'; 
                const parent = e.target.parentElement;
                if (parent) {
                  parent.innerHTML = '<span style="font-size:0.75rem;color:#999">IMG</span>';
                }
              }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">IMG</Typography>
          )}
          {/* Veg indicator */}
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              width: 18,
              height: 18,
              border: `2px solid ${product.isVeg ? '#0f8a0f' : '#e43b4f'}`,
              borderRadius: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.95)'
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: product.isVeg ? '#0f8a0f' : '#e43b4f'
              }}
            />
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
            {product.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {product.category}
          </Typography>
          
          {/* Combo/Special Product List */}
          {hasProductList && (
            <Box sx={{ mb: 1, p: 1, bgcolor: '#f9fafb', borderRadius: 1 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {isCombo ? 'Includes:' : 'Items:'}
              </Typography>
              <Stack spacing={0.25}>
                {product.productList.slice(0, 3).map((item, idx) => (
                  <Typography key={idx} variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    • {item.productName || 'Item'} {item.qty ? `(${item.qty})` : ''}
                  </Typography>
                ))}
                {product.productList.length > 3 && (
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: primary, fontWeight: 600 }}>
                    +{product.productList.length - 3} more items
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" fontWeight={800}>
              {currencySymbol} {product.price.toFixed(2)}
            </Typography>
            
            {/* Show quantity controls if item is in cart, otherwise show ADD button */}
            {qty > 0 ? (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onDecrement) onDecrement(product);
                  }}
                  sx={{ bgcolor: primary, color: '#fff', width: 24, height: 24, '&:hover': { bgcolor: primary } }}
                >
                  <RemoveIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <Typography variant="body2" fontWeight={700} sx={{ minWidth: 24, textAlign: 'center' }}>
                  {qty}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onIncrement) onIncrement(product);
                  }}
                  sx={{ bgcolor: primary, color: '#fff', width: 24, height: 24, '&:hover': { bgcolor: primary } }}
                >
                  <AddIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Stack>
            ) : (isHovered || isAnimating) ? (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToCartInternal(product);
                }}
                sx={{
                  width: 60,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <Lottie
                  lottieRef={isAnimating ? lottieRef : undefined}
                  animationData={addToCartAnimation}
                  loop={isAnimating}
                  autoplay={true}
                  style={{ width: 60, height: 60 }}
                />
              </Box>
            ) : (
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToCartInternal(product);
                }}
                sx={{
                  borderColor: primary,
                  color: primary,
                  fontSize: '0.75rem',
                  minWidth: 60,
                  '&:hover': {
                    borderColor: primary,
                    bgcolor: `${primary}10`
                  }
                }}
              >
                ADD
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderCart = () => {
    const { cart: cartConfig } = themeConfig;
    
    // Side cart for desktop
    if (!isMobile && (cartConfig.position === 'side' || cartConfig.position === 'side-panel' || cartConfig.position === 'elegant-side' || cartConfig.position === 'fixed-right')) {
      return (
        <Box sx={{ width: '100%', maxWidth: 300 }}>
          <Paper
            sx={{
              p: 2,
              borderRadius: cartConfig.borderRadius || 2,
              boxShadow: cartConfig.shadow,
              bgcolor: cartConfig.background,
              height: '100%'
            }}
          >
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 2 }}>
              Your Cart (0)
            </Typography>
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <ShoppingCartOutlinedIcon sx={{ fontSize: 40, color: 'rgba(0,0,0,0.2)', mb: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Cart is empty
              </Typography>
            </Box>
          </Paper>
        </Box>
      );
    }

    // Bottom bar for mobile
    if (isMobile && (cartConfig.type === 'sticky-bar' || cartConfig.type === 'fixed-bottom')) {
      return (
        <Paper
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            p: 1.5,
            boxShadow: cartConfig.shadow,
            bgcolor: cartConfig.background,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box>
            <Typography variant="caption" fontWeight={600}>0 items</Typography>
            <Typography variant="body2" fontWeight={800}>{currencySymbol} 0.00</Typography>
          </Box>
          <Button
            size="small"
            variant="contained"
            sx={{ bgcolor: primary, '&:hover': { bgcolor: primary } }}
          >
            View Cart
          </Button>
        </Paper>
      );
    }

    return null;
  };

  // Check if this is being used in public store (full page) vs preview (small panel)
  const isPublicStore = onCartClick !== undefined;
  
  return (
    <Box sx={{ 
      bgcolor: bgColor, 
      minHeight: isPublicStore ? '100vh' : '100%',
      height: isPublicStore ? 'auto' : '100%',
      display: 'flex', 
      flexDirection: 'column', 
      overflow: isPublicStore ? 'visible' : 'hidden',
      maxWidth: isPublicStore ? '1400px' : '100%',
      width: '100%',
      mx: isPublicStore ? 'auto' : 0
    }}>
      {/* Top Banner Carousel */}
      {carouselBanners.length > 0 && (
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: isMobile ? '200px' : '400px',
              overflow: 'hidden'
            }}
          >
            {/* Banner Slides */}
            {carouselBanners.map((banner, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: currentBannerIndex === index ? 1 : 0,
                  transition: 'opacity 0.5s ease-in-out',
                  pointerEvents: currentBannerIndex === index ? 'auto' : 'none'
                }}
              >
                {banner?.url ? (
                  banner.type === 'video' ? (
                    <video
                      ref={currentBannerIndex === index ? videoRef : null}
                      src={getImageUrl(banner.url)}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      muted
                      autoPlay={currentBannerIndex === index}
                      onEnded={handleVideoEnd}
                      onError={(e) => { 
                        console.error('Banner video failed to load:', banner.url);
                        e.target.style.display = 'none'; 
                      }}
                    />
                  ) : (
                    <img
                      src={getImageUrl(banner.url)}
                      alt={`Banner ${index + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { 
                        console.error('Banner image failed to load:', banner.url);
                        e.target.style.display = 'none'; 
                      }}
                    />
                  )
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="caption" color="text.secondary">Banner {index + 1}</Typography>
                  </Box>
                )}
              </Box>
            ))}

            {/* Navigation Arrows */}
            {carouselBanners.length > 1 && (
              <>
                <IconButton
                  onClick={prevBanner}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: '#fff',
                    width: 32,
                    height: 32,
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                  }}
                  size="small"
                >
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={nextBanner}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: '#fff',
                    width: 32,
                    height: 32,
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                  }}
                  size="small"
                >
                  <NavigateNextIcon fontSize="small" />
                </IconButton>

                {/* Dots Indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 0.5
                  }}
                >
                  {carouselBanners.map((_, index) => (
                    <Box
                      key={index}
                      onClick={() => setCurrentBannerIndex(index)}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: currentBannerIndex === index ? '#fff' : 'rgba(255,255,255,0.5)',
                        cursor: 'pointer',
                        transition: 'all 0.3s'
                      }}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Categories */}
      {displayCategories && displayCategories.length > 0 && (
        <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
            {displayCategories.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                size="small"
                onClick={() => onCategoryChange && onCategoryChange(cat)}
                variant={activeCategory === cat ? 'filled' : 'outlined'}
                sx={{
                  bgcolor: activeCategory === cat ? primary : 'transparent',
                  color: activeCategory === cat ? '#fff' : 'inherit',
                  borderColor: activeCategory === cat ? primary : 'rgba(0,0,0,0.15)',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: activeCategory === cat ? primary : `${primary}10`
                  }
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ 
        p: isPublicStore ? (isMobile ? 1.5 : 3) : 2, 
        height: isPublicStore ? 'auto' : 'calc(100% - 140px)', 
        overflowY: isPublicStore ? 'visible' : 'auto',
        overflowX: isPublicStore ? 'visible' : 'hidden',
        pb: isPublicStore ? 8 : 2
      }}>
        <Box sx={{ display: 'flex', gap: 2, minWidth: 0, flexWrap: isPublicStore ? 'nowrap' : 'wrap' }}>
          {/* Products */}
          <Box sx={{ flex: 1, minWidth: 0 }}>

            {/* Special Items Section */}
            {specialItems.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 4, height: 20, bgcolor: '#ff9800', borderRadius: 1 }} />
                  <Typography variant="body2" fontWeight={800} sx={{ color: '#ff9800' }}>
                    ⭐ Special Items
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: isPublicStore 
                      ? (isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(280px, 1fr))')
                      : (isMobile ? '1fr' : 'repeat(2, 1fr)'),
                    gap: isPublicStore ? (isMobile ? 1.5 : 2) : 1.5
                  }}
                >
                  {specialItems.slice(0, 4).map(renderProductCard)}
                </Box>
              </Box>
            )}

            {/* Combo Deals Section - Horizontal Scrollable */}
            {comboDeals.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 4, height: 20, bgcolor: '#9c27b0', borderRadius: 1 }} />
                  <Typography variant="body2" fontWeight={800} sx={{ color: '#9c27b0' }}>
                    🎁 Combo Deals
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    pb: 1,
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 3 }
                  }}
                >
                  {comboDeals.map((combo) => (
                    <Paper
                      key={combo._id || combo.id}
                      sx={{
                        minWidth: 280,
                        maxWidth: 280,
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        position: 'relative',
                        flexShrink: 0
                      }}
                    >
                      {/* Price Badge */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          bgcolor: '#d32f2f',
                          color: '#fff',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontWeight: 800,
                          fontSize: '0.9rem',
                          zIndex: 1,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}
                      >
                        {currencySymbol} {combo.price.toFixed(2)}
                      </Box>

                      {/* Image */}
                      <Box
                        sx={{
                          height: 140,
                          bgcolor: '#f5f5f5',
                          backgroundImage: combo.imageUrl ? `url(${getImageUrl(combo.imageUrl)})` : 'none',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      />

                      {/* Content */}
                      <Box sx={{ p: 1.5 }}>
                        <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                          {combo.name}
                        </Typography>

                        {/* Product List */}
                        {Array.isArray(combo.productList) && combo.productList.length > 0 && (
                          <Box sx={{ mb: 1 }}>
                            {combo.productList.map((item, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  py: 0.5,
                                  borderBottom: idx < combo.productList.length - 1 ? '1px solid #f0f0f0' : 'none'
                                }}
                              >
                                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#333' }}>
                                  {item.productName || 'Item'}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>
                                  {item.qty || 1}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}

                        {/* Add Button */}
                        <Button
                          fullWidth
                          size="small"
                          variant="contained"
                          sx={{
                            bgcolor: primary,
                            color: '#fff',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            '&:hover': { bgcolor: primary }
                          }}
                        >
                          ADD TO CART
                        </Button>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}

            <Typography variant="caption" fontWeight={800} sx={{ mb: 1, display: 'block' }}>
              Popular Items
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: isPublicStore 
                  ? (isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(280px, 1fr))')
                  : (isMobile 
                      ? `repeat(${themeConfig.grid.columns.xs || 1}, 1fr)`
                      : `repeat(${Math.min(themeConfig.grid.columns.md || 2, 2)}, 1fr)`),
                gap: isPublicStore ? (isMobile ? 1.5 : 2) : (themeConfig.grid.spacing || 2)
              }}
            >
              {displayProducts.map((product, idx) => (
                <React.Fragment key={product.id || idx}>
                  {renderProductCard(product)}
                  {/* Render banners after specific rows */}
                  {(idx + 1) % 3 === 0 && (idx + 1) === 3 && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      {renderPlacementBanner('after-row-3')}
                    </Box>
                  )}
                  {(idx + 1) % 6 === 0 && (idx + 1) === 6 && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      {renderPlacementBanner('after-row-6')}
                    </Box>
                  )}
                  {(idx + 1) % 9 === 0 && (idx + 1) === 9 && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      {renderPlacementBanner('after-row-9')}
                    </Box>
                  )}
                </React.Fragment>
              ))}
            </Box>

            {/* Bottom Placement Banners */}
            {renderPlacementBanner('bottom')}
          </Box>

          {/* Cart (desktop side cart only) */}
          {!isMobile && (themeConfig.cart.position === 'side' || themeConfig.cart.position === 'side-panel' || themeConfig.cart.position === 'elegant-side' || themeConfig.cart.position === 'fixed-right') && renderCart()}
        </Box>
      </Box>

      {/* Mobile cart bar */}
      {isMobile && renderCart()}
    </Box>
  );
};

export default LiveStorePreview;
